import { Injectable } from '@angular/core';
import { FeatureCollection, Feature, Geometry, Point, LineString } from 'geojson';
import { MathAux } from '../math-aux/math-aux';
import * as d3 from 'd3';
import * as turf from '@turf/turf';
import { NormalizerAux } from '../normalizer-aux/normalizer-aux';
import { ContinuousMapPainterService } from '../continuous-map-painter/continuous-map-painter.service';
import { DiscreteMapPainterService } from '../discrete-map-painter/discrete-map-painter.service';
import { Map, Marker, Popup } from 'maplibre-gl';
import { ColorType } from '../../models/color-type';
import { GeojsonAuxService } from '../geojson-aux/geojson-aux.service';
import { PaintStrategy } from '../../utils/paint-strategy';
import { LayerParams } from '../../map/layer-manager';
import { getPredictedRainValue, getStationWeights, interpolateRain } from '../../utils/rain-interpolator';


@Injectable({
  providedIn: 'root'
})
export class MapManagerService {

  hoveredStateId: string = null;
  range: number[];
  continuousMiddleData: number[];
  markers: Marker[] = [];
  grid: turf.helpers.FeatureCollection<turf.helpers.Polygon, {
    [name: string]: any;
  }>;
  eventMarkers: { [key: string]: Marker[] } = {};
  timeIntervals = ["indice_pluv", "quinzemin", "trintamin", "umahora", "seishoras", "dozehoras", "vintequatrohoras", "quarentaoitohoras", "setentaduashoras", "noventaseishoras", "mes"]
  rainDivisors: { timestamps: string[], divisor: number[] } = {
    timestamps: ["indice_pluv", "quinzemin", "trintamin", "umahora", "seishoras", "dozehoras", "vintequatrohoras", "quarentaoitohoras", "setentaduashoras", "noventaseishoras", "mes"],
    divisor: [1 / 4, 1 / 4, 1 / 2, 1, 6, 12, 24, 48, 72, 96, 720]
  };
  discreteColors = ['#339944', '#FFCC00', '#FF8822', '#DD2233'];
  eventColors: { [key: string]: string } = {
    'DESLIZAMENTO': '#664422',
    'ALAGAMENTO': '#6600EE',
    'DESABAMENTO': '#505050',
  };

  routePopup = new Popup({
    closeButton: false,
    closeOnClick: false
  });

  regionBuffer: {
    grid: turf.helpers.FeatureCollection<turf.helpers.Point, {
      [name: string]: any;
    }>[], highway: Feature<LineString>[][]
  } = { grid: undefined, highway: undefined };

  constructor(
    private continuousPainter: ContinuousMapPainterService,
    private discretePainter: DiscreteMapPainterService,
    private geojsonAux: GeojsonAuxService
  ) { }

  /**
   * Inicializa os dados das estações.
   * @param rainData dados das estações em um instante específico.
   * @param percentil array [ percentil inferior, percentil superior ].
   * @param highway_type número identificador da camada.
   * @param timestampArray nomes das colunas dos acumulados de chuva.
   * @returns dados das estações com cores calculadas e coordenadas ajustadas.
   */
  initialize(rainData: FeatureCollection<Point>, selectedTimestamp: string, 
    percentil: number[], highway_type: ColorType, timestampArray: string[]) {
    
    this.prepareStationsData(rainData, timestampArray);
    this.sortStationsData(rainData, selectedTimestamp);
    this.createAllTimeStampColors(rainData, percentil, highway_type, timestampArray);
    return rainData;
  }

  // ==================================================================================================
  // ===================================== Sources e Layers ===========================================
  // ==================================================================================================

  /**
   * Set mapbox instance data source
   * @see https://docs.mapbox.com/mapbox-gl-js/example/geojson-polygon/
   * @param map mapbox instance which will be configured
   * @param dataSourceId data source exclusive identifier
   * @param dataSourceUrl data endpoint url from which data will be fetched
   */
  setMapSource(map: Map, dataSourceId: string, dataSourceUrl: FeatureCollection<Geometry>) {
    // first, we add a data source with 'data' attribute pointing to some data end point
    map.addSource(dataSourceId, {
      type: 'geojson',
      data: dataSourceUrl,
      promoteId: 'ogc_fid'
    });
  }

  setRouteMapSource(map: Map, dataSourceId: string, dataSourceUrl: FeatureCollection<Geometry>) {
    // first, we add a data source with 'data' attribute pointing to some data end point
    console.log('dataSourceURL: ', dataSourceUrl);
    map.addSource(dataSourceId, {
      type: 'geojson',
      data: dataSourceUrl,
      promoteId: 'id',
      lineMetrics: true,
    });
  }

  /**
   * set mapbox instance fill layer
   * @see https://docs.mapbox.com/mapbox-gl-js/example/geojson-polygon/
   * @param map mapbox instance which will be configured
   * @param dataSourceId data source exclusive identifier
   * @param dataLayerFillId fill type layer exclusive identifier
   * @param fillColor layer fill color
   * @param fillOpacity layer fill opacity
   */
  setMapFillLayer(map: Map, dataSourceId: string, dataLayerFillId: string,
    fillColor: string = 'rgba(0, 0, 0, 0)', lineColor: string) {
    // second, we add a type fill layer to 'paint' the regions on the map
    map.addLayer({
      id: dataLayerFillId,
      type: 'fill',
      source: dataSourceId,
      layout: {
        visibility: 'visible',
      },
      paint: {
        'fill-color': fillColor,
        'fill-outline-color': lineColor
      },
    });
  }

  /**
   * Set mapbox instance line layer
   * @see https://docs.mapbox.com/mapbox-gl-js/example/geojson-polygon/
   * @param map mapbox instance which will be configured
   * @param dataSourceId data source exclusive identifier
   * @param dataLayerLineId line type layer exclusive identifier
   * @param lineColor layer line color
   * @param lineOpacity layer line opacity
   * @param lineWidth layer line width
   */
  setMapLineLayer(map: Map, dataSourceId: string, dataLayerLineId: string,
    lineColor: string, lineOpacity: number, lineWidth: number) {
    // lastly, we add a type outline layer to draw the boundaries of the reigions
    if (map.getLayer(dataLayerLineId)) {
      map.removeLayer(dataLayerLineId);
    }

    map.addLayer({
      id: dataLayerLineId,
      type: 'line',
      source: dataSourceId,
      layout: {
        visibility: 'visible',
      },
      paint: {
        'line-color': lineColor,
        'line-opacity': 1,
        'line-width': lineWidth,
      },
    });
  }

  setRouteMapLineLayer(map: Map, dataSourceId: string, dataLayerLineId: string,
    lineColor: string, lineOpacity: number, lineWidth: number) {
    // lastly, we add a type outline layer to draw the boundaries of the reigions
    if (map.getLayer(dataLayerLineId)) {
      map.removeLayer(dataLayerLineId);
    }

    map.addLayer({
      id: dataLayerLineId,
      type: 'line',
      source: dataSourceId,
      layout: {
        visibility: 'visible',
      },
      paint: {
        'line-color': lineColor,
        'line-gradient': [
          'interpolate',
          ['linear'],
          ['line-progress'],
          0,
          '#efdada',
          1,
          '#ed0a0a'
        ],
        'line-opacity': ['case',
          ['boolean', ['feature-state', 'hover'], false],
          0.7,
          1
        ],
        'line-width': lineWidth,
      }
    });
  }
  
  /**
   * Adiciona uma camada de linha do histórico de rotas.
   * Não é o mesmo que uma camada de linha de uma rota da heurística executada
   * correntemente. Para esse caso, veja setRouteMapLineLayer.
   * 
   * @param map 
   * @param dataSourceId 
   * @param dataLayerLineId 
   * @param dataLayerPointId 
   * @param lineColor 
   * @param lineOpacity 
   * @param lineWidth 
   */
  setHistoryLayer(map: Map, dataSourceId: string, dataLayerLineId: string,
    dataLayerPointId: string, lineColor: string, lineOpacity: number, lineWidth: number) {
    if (map.getLayer(dataLayerLineId)) {
      map.removeLayer(dataLayerLineId);
    }

    map.addLayer({
      'id': dataLayerLineId,
      'type': 'line',
      'source': dataSourceId,
      'layout': {
        'visibility': 'visible',
      },
      'paint': {
        'line-color': [
          'match',
          ['get', 'iterationGroup'],
          0,
          '#ff0000',
          1,
          '#00ff00',
          '#0000ff',
        ],
        // 'line-gradient': [
        //   'interpolate',
        //   ['linear'],
        //   ['line-progress'],
        //   0,
        //   '#efdada',
        //   1,
        //   '#1126ba'
        // ],
        'line-opacity': ['case',
          ['boolean', ['feature-state', 'hover'], false],
          0.7,
          1
        ],
        'line-width': lineWidth,
      },
      'filter': ['==', '$type', 'LineString']
    });
    
    map.addLayer({
      'id': dataLayerPointId,
      'type': 'circle',
      'source': dataSourceId,
      'paint': {
        'circle-radius': 6,
        'circle-color': [
          'match',
          ['get', 'iterationGroup'],
          0,
          '#ff0000',
          1,
          '#00ff00',
          '#0000ff',
        ],
      },
      'filter': ['==', '$type', 'Point']
    });
  }
  
  setVisibilityHistoryLayer(map: Map, checked: boolean, routeId: string, vehicleId: string) {
    if (!map.getLayer(routeId) || !map.getLayer(vehicleId)) {
      return;
    }
    
    if (checked) {
      map.setLayoutProperty(routeId, 'visibility', 'visible');
      map.setLayoutProperty(vehicleId, 'visibility', 'visible');
    } else {
      map.setLayoutProperty(routeId, 'visibility', 'none');
      map.setLayoutProperty(vehicleId, 'visibility', 'none');
    }
  }
  
  setVisibilityCurrentLayer(map: Map, checked: boolean, routeId: string) {
    if (!map.getLayer(routeId)) {
      return;
    }
    
    if (checked) {
      map.setLayoutProperty(routeId, 'visibility', 'visible');
    } else {
      map.setLayoutProperty(routeId, 'visibility', 'none');
    }
  }

  /**
   * Set mouse events for line layer
   * @param map mapbox instance which will be configured
   * @param dataSourceId data source exclusive identifier
   * @param dataLayerLineId line type layer exclusive identifier
   */
  setRouteMouseEvents(map: Map, dataSourceId: string, dataLayerLineId: string) {
    map.on('mousemove', dataLayerLineId, (e) => {
      
      // console.log('dataSourceId: ', dataSourceId);
      // console.log('geometry: ', e.features[0].geometry);
      // console.log(e.features);
      // console.log(e.features[0].properties.id);
      // console.log('hoveredStateId: ', this.hoveredStateId);

      map.getCanvas().style.cursor = 'pointer';
      

      // const coordinatess = (e.features[0].geometry as MultiLineString).coordinates[Object.keys(e.features[0]).length]

      const coordinates = (e.features[0].geometry as LineString).coordinates[
        Math.floor(Object.keys((e.features[0].geometry as LineString).coordinates).length / 2)
      ];
      
      if (e.features[0].geometry.type === 'LineString' || e.features[0].geometry.type === 'MultiLineString') {
        const coordinatesMls = e.features[0].geometry.coordinates.slice();
      }
      // console.log('coordinates mls: ', coordinatess);
      



      // Popup information description
      const description = e.features[0].properties.description;
      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }
      this.routePopup.setLngLat([coordinates[0], coordinates[1]]).setHTML(description).addTo(map);

      // Change hover status to true
      if (e.features.length > 0) {
        if (this.hoveredStateId !== null) {
          map.setFeatureState(
            { source: dataSourceId, id: this.hoveredStateId },
            { hover: false }
          );
        }
        this.hoveredStateId = '' + e.features[0].id;
        console.log("hoveredStateId: ", this.hoveredStateId);
        map.setFeatureState(
          { source: dataSourceId, id: this.hoveredStateId },
          { hover: true }
        );
      }
    });

    map.on('mouseleave', dataLayerLineId, () => {
      map.getCanvas().style.cursor = '';
      this.routePopup.remove();

      if (this.hoveredStateId !== null) {
        map.setFeatureState(
          { source: dataSourceId, id: this.hoveredStateId },
          { hover: false }
        );
      }
      this.hoveredStateId = null;
    });
  }

  /**
   * Carrega a layer de linhas com lógica para colorir as vias de acordo com os níveis de chuva.
   * Esses níveis de chuva das vias são carregados posteriormente.
   * 
   * @param segments_url url de busca das vias
   * @param lineSourceId id da data-source das vias
   * @param lineLayerId id da layer das linhas das vias
   */
  loadSegmentLayer(map: Map, lineSourceId: string, lineLayerId: string) {

    if (map.getLayer(lineLayerId)) {
      map.removeLayer(lineLayerId);
    }

    // Colored Segment layer
    map.addLayer({
      'id': lineLayerId,
      'type': 'line',
      'source': lineSourceId,
      'paint': {
        'line-color': [
          'case',
          ['!=', ['feature-state', 'rainHeatmap'], null], //if
          ['feature-state', 'rainColor'], //then
          'white' //else
        ],
        'line-width': 1
      }
    });
  }

  /**
   * Remove as layers de heatmap que estejam ativas no mapa
   * 
   * @param map instância do mapbox cujas layers serão removidas
   */
  removeLayers(map: Map, markers: Marker[]) {

    if (map.getLayer('chuvas-heat')) {
      map.removeLayer('chuvas-heat');
    }

    if (map.getLayer('chuvas-grid')) {
      map.removeLayer('chuvas-grid');
    }

    if (map.getLayer('highlighted_station_layer')) {
      map.removeLayer('highlighted_station_layer');
    }

    if (map.getLayer('highlighted_neighborhood_layer_line')) {
      map.removeLayer('highlighted_neighborhood_layer_line');
    }
    if (map.getLayer('highlighted_neighborhood_layer_fill')) {
      map.removeLayer('highlighted_neighborhood_layer_fill');
    }

    markers.forEach((val) => {
      val.remove();
    })

  }

  /**
   * Remove as data-sources de heatmap que estejam ativas no mapa
   * 
   * @param map instância do mapbox cujas data-sources serão removidas
   */
  removeSources(map: Map) {

    if (map.getSource('chuvas')) {
      map.removeSource('chuvas');
    }

    if (map.getSource('chuvas-point')) {
      map.removeSource('chuvas-point');
    }

    if (map.getSource('chuvas-grid')) {
      map.removeSource('chuvas-grid');
    }

    if (map.getSource('highlighted_station_source')) {
      map.removeSource('highlighted_station_source');
    }

    if (map.getSource('highlighted_neighborhood_source')) {
      map.removeSource('highlighted_neighborhood_source');
    }


  }

  /**
   * Remove os marcadores das estações do mapa. Importante remover os marcadores de um horário
   * antes de inserir os marcadores de um novo horário, a fim de evitar que os marcadores dos
   * dois horários diferentes se misturem.
   */
  removeMarkers() {

    this.markers.forEach(marker => marker.remove());
    this.markers = [];
  }

  // ==================================================================================================
  // ===================================== Ajustes dos dados ==========================================
  // ==================================================================================================

  /**
   * Ajusta atributos do objeto que possui os dados das estações, resolvendo problemas associados a
   * índices pluviométricos problemáticos e coordenadas trocadas
   * 
   * @param rainData dados das estações
   * @param timestampArray array com os nomes das resoluções de tempo (p. ex. indice_pluv, quinzemin), 
   *    que são propriedades dos registros das estações.
   */
   prepareStationsData(rainData: FeatureCollection<Point>, timestampArray: string[]) {
    
    // Ajustar a geometria, porque há casos em que a longitude vem antes da latitude no array 
    // de coordenadas.
    rainData.features.forEach((val,index)=>{

      if (val.geometry.coordinates[0] > val.geometry.coordinates[1]) {
        let auxcord = 0;
        auxcord = val.geometry.coordinates[0];
        rainData.features[index].geometry.coordinates[0] = val.geometry.coordinates[1];
        rainData.features[index].geometry.coordinates[1] = auxcord;
      }
    });

    // Arredondando os acumulados de chuva para 4 casas decimais. Se o valor for null, ele deve ser
    // mantido como null, pois simboliza a ausência do registro da estação no tempo específico.
    timestampArray.forEach( (timeStamp)=>{  
        rainData.features.forEach((val)=>{
          if (val.properties[timeStamp] != null) {
            val.properties[timeStamp] = MathAux.round(val.properties[timeStamp],4);
          }
      });
    });
  }

  /**
   * Ordena as estações pelo índice pluviométrico em ordem crescente.
   * 
   * @param rainData dados das estações a serem ordenadas
   */
  sortStationsData(rainData: FeatureCollection<Point>, timestamp: string) {
    rainData.features.sort((a, b) => { return parseFloat(a.properties[timestamp]) - parseFloat(b.properties[timestamp]) });
  }

  /**
   * Adiciona o atributo avg às estações, cujo valor é calculado a partir do índice pluviométrico e está 
   * entre 0 e 1, para permitir o cálculo da grade de interpolação pelo módulo turf.
   * 
   * @param rainData dados das estações que receberão o atributo avg
   * @param normalizer normalizador do d3js, construído a partir dos índices pluviométricos que estão 
   *  entre os percentis selecionados pelo usuário.
   */
  normalizeStationsIndicePluv(rainData: FeatureCollection<Point>,
    normalizer: d3.ScaleLinear<number, number, never>, timestamp: string) {


    rainData.features.forEach((val) => {
      if (val.properties[timestamp] == 0) {
        val.properties.avg = 0
      } else {
        val.properties.avg = normalizer(parseFloat(val.properties[timestamp]));
      }
    });
  }

  /**
   * Obtém os índices pluviométricos das estações
   * 
   * @param rainData dados das estações
   */
  getIndicesPluv(rainData: FeatureCollection<Point>, timestamp: string): number[] {

    return rainData.features.map(station => parseFloat(station.properties[timestamp]));
  }

  /**
   * Adiciona bordas auxiliares para adicionar um "padding" à grade da interpolação que será gerada
   * pelo turf.
   * São adicionadas uma borda superior esquerda e uma borda inferior direita, ambas com o 
   * índice pluviométrico igual a -1, o id de tempo igual a 999999 e o valor avg igual a 0. 
   * 
   * @param rainData dados das estações às quais serão adicionadas duas estações artificiais auxiliares.
   */
  addBorderStations(rainData: FeatureCollection<Point>) {

    let stations = rainData.features;
    let lefttop = [Number.MAX_SAFE_INTEGER, Number.MIN_SAFE_INTEGER];
    let rightbot = [Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER];

    stations.forEach((values) => {

      if (values.geometry.coordinates[0] < lefttop[0]) {
        lefttop[0] = values.geometry.coordinates[0];
      }
      if (values.geometry.coordinates[1] > lefttop[1]) {
        lefttop[1] = values.geometry.coordinates[1];
      }
      if (values.geometry.coordinates[0] > rightbot[0]) {
        rightbot[0] = values.geometry.coordinates[0];
      }
      if (values.geometry.coordinates[1] < rightbot[1]) {
        rightbot[1] = values.geometry.coordinates[1];
      }
    });

    stations.push({
      'type': 'Feature', 'geometry': { 'type': 'Point', 'coordinates': [lefttop[0] - 0.03, lefttop[1] + 0.03] }, 'properties': {
        'id_tempo': 999999,
        "indice_pluv": -1, "quinzemin": -1, "trintamin": -1, "umahora": -1, "seishoras": -1, "dozehoras": -1, "vintequatrohoras": -1, "quarentaoitohoras": -1, "setentaduashoras": -1, "noventaseishoras": -1, "mes": -1,
        'total': -1, 'percent': -1, 'nfraca': -1, 'nmoderada': -1, 'nforte': -1, 'nmuitoforte': -1, 'mean': -1, 'mode': -1, 'median': -1, 'avg': 0
      }
    });
    stations.push({
      'type': 'Feature', 'geometry': { 'type': 'Point', 'coordinates': [rightbot[0] + 0.046, rightbot[1] - 0.03] }, 'properties': {
        'id_tempo': 999999,
        "indice_pluv": -1, "quinzemin": -1, "trintamin": -1, "umahora": -1, "seishoras": -1, "dozehoras": -1, "vintequatrohoras": -1, "quarentaoitohoras": -1, "setentaduashoras": -1, "noventaseishoras": -1, "mes": -1,
        'total': -1, 'percent': -1, 'nfraca': -1, 'nmoderada': -1, 'nforte': -1, 'nmuitoforte': -1, 'mean': -1, 'mode': -1, 'median': -1, 'avg': 0
      }
    });
  }

  // ==================================================================================================
  // ===================================== Exibição dos dados =========================================
  // ==================================================================================================


  /**
   * Colore os pontos para cada resolução de tempo do acumulado de chuva.
   * 
   * @param toBePainted pontos a serem coloridos (normalmente estações ou centroides dos bairros).
   * @param percentil array [percentil inferior, percentil superior].
   * @param colorType tipo de coloração (contínua ou discreta).
   * @param timestampArray array com nomes das resoluções de tempo dos acumulados de chuva. 
   * @param colorationData pontos cujos acumulados de chuva podem ser usados para colorir os pontos. Por exemplo, usar os acumulados das estações para colorir os bairros.
   */
  createAllTimeStampColors(
    toBePainted: FeatureCollection<Point>,
    percentil: number[],
    colorType: ColorType,
    timestampArray: string[],
    colorationData?: FeatureCollection<Point>
  ) {
    // Gerar as cores para cada resolução de tempo.
    timestampArray.forEach((timestamp, index) => {

      let indicesPluvSource = (colorationData == null) ? toBePainted : colorationData;
      let indicesPluv = this.getIndicesPluv(indicesPluvSource, timestamp);
      let middleIndicesPluv = MathAux.createMiddleData(indicesPluv, percentil);

      let layerParams: LayerParams = {
        colorType,
        middleIndicesPluv,
        rainData: toBePainted,
        selectedTimestamp: timestamp,
      };
      let painter = PaintStrategy.factory(layerParams);

      // Colorir os pontos na resolução de tempo atual.
      toBePainted.features.forEach((feature, index) => {

        const { properties } = feature;
        if (!properties.hasOwnProperty('color')) {
          properties.color = {};
        }

        properties.color[timestamp] = painter(properties[timestamp]);
      });
    });
  }

  pointFromPolygonUsingHighways(polygon: any, name: string, data: FeatureCollection<Point>, color: ColorType, timeColums: { name: string, text: string }[], highwaysData: Feature<Geometry>[], percentil?: number[],
  ): Feature<Point> {
    let stations: FeatureCollection<Point> = MathAux.deepCopy(data) as FeatureCollection<Point>
    let highways: Feature<LineString>[] = MathAux.deepCopy(highwaysData) as Feature<LineString>[]
    let features: Feature<LineString>[];

    let newGeojson: Feature<Point> = {
      "type": "Feature",
      "id": polygon.id,
      "geometry": {
        "type": "Point",
        "coordinates": polygon.geometry.coordinates[0]
      },
      "properties": {
        "color": {},
        "region": true
      }
    }
    features = this.geojsonAux.getFeaturesInsidePolygon(highways, polygon);
    features = features.slice(0, Math.min(features.length, 50));
    timeColums.forEach((timestamp, index) => {
      let middleValues: number[];
      interpolateRain(stations, features, 2, timestamp.name)
      let sum: number = 0
      features.forEach(val => { sum += val.properties.rain_value })
      newGeojson.properties[timestamp.name] = (features.length > 0) ? sum / features.length : 0
      let typecolor
      if (color == 2) {
        let indice = newGeojson.properties[timestamp.name] / this.rainDivisors.divisor[index]
        if (indice < 2.5)
          typecolor = "#339944"
        else if (indice < 10)
          typecolor = "#FFCC00"
        else if (indice < 50)
          typecolor = "#FF8822"
        else
          typecolor = "#DD2233"
      } else {
        let middlearray
        this.sortStationsData(stations, timestamp.name)
        middlearray = this.getIndicesPluv(stations, timestamp.name)
        middleValues = MathAux.createMiddleData(middlearray, percentil)
        let normalizer = NormalizerAux.createNormalizer(middleValues, (p: number) => p);
        this.continuousPainter.setNormalizer(normalizer);

        typecolor = this.continuousPainter.getColor(
          newGeojson.properties[timestamp.name],
          middleValues,
          "#FFF"
        )
      }
      newGeojson.properties.color[timestamp.name] = typecolor
    })
    newGeojson.properties.nome_estacao_original = name
    return newGeojson
  }

  pointFromPolygonUsingGrid(polygon: any, name: string, data: FeatureCollection<Point>, color: ColorType, timeColums: { name: string, text: string }[], percentil?: number[]
  ): Feature<Point> {
    let stations: FeatureCollection<Point> = MathAux.deepCopy(data) as FeatureCollection<Point>
    let grids: Feature<Geometry>[];

    let newGeojson: Feature<Point> = {
      "type": "Feature",
      "id": polygon.id,
      "geometry": {
        "type": "Point",
        "coordinates": polygon.geometry.coordinates[0]
      },
      "properties": {
        "color": {},
        "region": true
      }
    }
    this.addBorderStations(stations)
    let grid = turf.interpolate(stations, 0.5, { gridType: 'point', property: timeColums[0].name, units: 'kilometers', weight: 2 });
    grids = this.geojsonAux.getGridsInsidePolygon(grid, polygon);
    timeColums.forEach((timestamp, index) => {
      let middleValues;
      grids.forEach((elem: any) => {
        let sum_weights = getStationWeights(stations, elem.geometry.coordinates[1], elem.geometry.coordinates[0], 2);
        let predicted_value = getPredictedRainValue(stations, sum_weights, timestamp.name);
        elem.properties.rain_value = MathAux.round(predicted_value, 4);
      });
      let sum: number = 0
      grids.forEach(val => { sum += val.properties.rain_value })
      newGeojson.properties[timestamp.name] = (grids.length > 0) ? sum / grids.length : 0
      let typecolor
      if (color == 2) {
        let indice = newGeojson.properties[timestamp.name] / this.rainDivisors.divisor[index]
        if (indice < 2.5)
          typecolor = "#339944"
        else if (indice < 10)
          typecolor = "#FFCC00"
        else if (indice < 50)
          typecolor = "#FF8822"
        else
          typecolor = "#DD2233"
      } else {
        let middlearray
        this.sortStationsData(stations, timestamp.name)
        let nonBorderStations = stations.features.filter(station => station.properties.indice_pluv > -1);
        middlearray = this.getIndicesPluv({ "type": "FeatureCollection", "features": nonBorderStations }, timestamp.name)

        middleValues = MathAux.createMiddleData(middlearray, percentil)
        let normalizer = NormalizerAux.createNormalizer(middleValues, (p: number) => p);
        this.continuousPainter.setNormalizer(normalizer);

        typecolor = this.continuousPainter.getColor(
          newGeojson.properties[timestamp.name],
          middleValues,
          "#FFF"
        )
      }
      newGeojson.properties.color[timestamp.name] = typecolor
    })
    newGeojson.properties.nome_estacao_original = name
    return newGeojson
  }

  pointFromPolygonNeightborhood(polygon: any[], name: string, data: FeatureCollection<Point>, timeColums: { name: string, text: string }[], maxPolIndex: number, percentil?: number[],
  ): Feature<Point> {
    let stations: FeatureCollection<Point> = MathAux.deepCopy(data) as FeatureCollection<Point>

    let newGeojson: Feature<Point> = {
      "type": "Feature",
      "id": polygon[0].properties.ogc_fid,
      "geometry": {
        "type": "Point",
        "coordinates": polygon[0].geometry.coordinates[0]
      },
      "properties": {
        "color": {},
        "neighborhood": true
      }
    }
    this.addBorderStations(stations)
    timeColums.forEach((timestamp) => {
      let sum = 0;
      polygon.forEach((currentPol, index) => {
        let center = turf.centroid(currentPol)
        let sum_weights = getStationWeights(stations, center.geometry.coordinates[1], center.geometry.coordinates[0], 2);
        let predicted_value = getPredictedRainValue(stations, sum_weights, timestamp.name);
        center.properties.rain_value = MathAux.round(predicted_value, 4);
        if (index == maxPolIndex) sum += (center.properties.rain_value * 2);
        else sum += center.properties.rain_value;
      })
      newGeojson.properties[timestamp.name] = sum / (polygon.length + 1)
    })
    newGeojson.properties.nome_estacao_original = name
    return newGeojson
  }

}
